import {
  DEMO_COMPANIES_ID_CHANGED_EVENT,
  DEMO_COMPANIES_ID_STORAGE_KEY,
  type DemoCompaniesId,
  type DemoCompanyEntityType,
  EMPTY_DEMO_COMPANIES_ID,
} from 'src/constants/demoDashboardMode';

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(item => typeof item === 'string');

let cachedSerialized: string | null = null;
let cachedSnapshot: DemoCompaniesId = EMPTY_DEMO_COMPANIES_ID;

const parseDemoCompaniesId = (raw: string | null): DemoCompaniesId => {
  if (!raw) return EMPTY_DEMO_COMPANIES_ID;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return EMPTY_DEMO_COMPANIES_ID;

    const record = parsed as Partial<DemoCompaniesId>;

    return {
      employers: isStringArray(record.employers) ? record.employers : [],
      peos: isStringArray(record.peos) ? record.peos : [],
      peoPayrollProviders: isStringArray(record.peoPayrollProviders)
        ? record.peoPayrollProviders
        : [],
    };
  } catch {
    return EMPTY_DEMO_COMPANIES_ID;
  }
};

const updateSnapshotCache = (serialized: string | null, snapshot: DemoCompaniesId) => {
  cachedSerialized = serialized;
  cachedSnapshot = snapshot;
};

export const readDemoCompaniesId = (): DemoCompaniesId => {
  try {
    const raw = localStorage.getItem(DEMO_COMPANIES_ID_STORAGE_KEY);
    return parseDemoCompaniesId(raw);
  } catch {
    return EMPTY_DEMO_COMPANIES_ID;
  }
};

export const getDemoCompaniesIdSnapshot = (): DemoCompaniesId => {
  try {
    const raw = localStorage.getItem(DEMO_COMPANIES_ID_STORAGE_KEY);
    const serialized = raw ?? '';

    if (serialized === cachedSerialized) {
      return cachedSnapshot;
    }

    const snapshot = parseDemoCompaniesId(raw);
    updateSnapshotCache(serialized, snapshot);
    return snapshot;
  } catch {
    return EMPTY_DEMO_COMPANIES_ID;
  }
};

export const getDemoCompaniesIdServerSnapshot = (): DemoCompaniesId => EMPTY_DEMO_COMPANIES_ID;

export const subscribeDemoCompaniesId = (onStoreChange: () => void) => {
  window.addEventListener(DEMO_COMPANIES_ID_CHANGED_EVENT, onStoreChange);
  window.addEventListener('storage', onStoreChange);

  return () => {
    window.removeEventListener(DEMO_COMPANIES_ID_CHANGED_EVENT, onStoreChange);
    window.removeEventListener('storage', onStoreChange);
  };
};

export const writeDemoCompaniesId = (ids: DemoCompaniesId) => {
  const serialized = JSON.stringify(ids);
  localStorage.setItem(DEMO_COMPANIES_ID_STORAGE_KEY, serialized);
  updateSnapshotCache(serialized, ids);
  window.dispatchEvent(new Event(DEMO_COMPANIES_ID_CHANGED_EVENT));
};

export const setDemoCompanyEnabled = (
  ids: DemoCompaniesId,
  entityType: DemoCompanyEntityType,
  entityId: string,
  enabled: boolean,
): DemoCompaniesId => {
  const nextIds = new Set(ids[entityType]);

  if (enabled) {
    nextIds.add(entityId);
  } else {
    nextIds.delete(entityId);
  }

  return {
    ...ids,
    [entityType]: [...nextIds],
  };
};

export const isDemoCompanyEnabled = (
  ids: DemoCompaniesId,
  entityType: DemoCompanyEntityType,
  entityId: string | null | undefined,
) => {
  return Boolean(entityId && ids[entityType].includes(entityId));
};
